/**
 * Utilitários para manipulação de mensagens
 */

import i18n from '@/i18n'
import {
  accountDayKey,
  addAccountDays,
  formatInAccountTz,
} from '@/lib/accountTimezone'

/**
 * Formata o horário da mensagem para exibição.
 *
 * Horários e a fronteira hoje/ontem seguem o fuso da conta (Settings → Geral), não o do
 * navegador: dois operadores em fusos diferentes precisam ver o mesmo rótulo.
 */
export const formatMessageTime = (sentAt: string): string => {
  const t = i18n.getFixedT(null, 'common')
  try {
    const date = new Date(sentAt)
    const now = new Date()

    const dayKey = accountDayKey(date)
    if (!dayKey) return t('time.invalidDate')

    // Se for hoje, mostrar apenas horário
    if (dayKey === accountDayKey(now)) {
      return formatInAccountTz(date, { hour: '2-digit', minute: '2-digit' })
    }

    // Se for ontem
    if (dayKey === accountDayKey(addAccountDays(now, -1))) {
      return t('time.yesterdayAt', {
        time: formatInAccountTz(date, { hour: '2-digit', minute: '2-digit' }),
      })
    }

    // Se for esta semana
    if (date > addAccountDays(now, -7)) {
      const dayName = formatInAccountTz(date, { weekday: 'short' })
      return `${dayName} ${formatInAccountTz(date, { hour: '2-digit', minute: '2-digit' })}`
    }

    // Mais antigo, mostrar data completa
    return formatInAccountTz(date, {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (error) {
    console.error('Erro ao formatar data da mensagem:', error)
    return t('time.invalidDate')
  }
}

/**
 * Formata o tipo de mensagem para exibição
 */
export const formatMessageType = (msgType: string): string => {
  const typeMap: Record<string, string> = {
    'conversation': 'Texto',
    'extendedTextMessage': 'Texto',
    'imageMessage': 'Imagem',
    'videoMessage': 'Vídeo',
    'audioMessage': 'Áudio',
    'documentMessage': 'Documento',
    'stickerMessage': 'Sticker',
    'locationMessage': 'Localização',
    'contactMessage': 'Contato',
    'reactionMessage': 'Reação',
    'protocolMessage': 'Sistema'
  }
  
  return typeMap[msgType] || msgType
}

/**
 * Verifica se a mensagem contém mídia
 */
export const hasMedia = (message: any): boolean => {
  const mediaTypes = [
    'imageMessage',
    'videoMessage', 
    'audioMessage',
    'documentMessage',
    'stickerMessage'
  ]
  
  return mediaTypes.some(type => message.msg_type === type)
}

/**
 * Gera preview do conteúdo da mensagem
 */
export const getMessagePreview = (message: any): string => {
  if (message.text_content) {
    return message.text_content.length > 50 
      ? message.text_content.substring(0, 50) + '...'
      : message.text_content
  }
  
  if (message.media_kind) {
    return `📎 ${formatMessageType(message.msg_type)}`
  }
  
  return '[Mensagem sem conteúdo]'
}